/**
 * There is a 1: relationship between company in CAN API and company in ERP API.
 * The only way as of now (2025) to connect the companies is using the country code and company number.
 *
 * Besides that, knowing ERP company is not required at all times. Specifically, when a newly registered user
 * comes to purchase prices screen, we want to show him the screen even if he does not have any company in ERP.
 *
 * ERP company is only linked with the user when the user has been approved as a member of such company.
 */

import { useSuspenseQuery } from "@tanstack/react-query";
import invariant from "tiny-invariant";

import { companiesQuery } from "@/routes/storages/api";

import { useCompany } from "../providers/company-provider";

const useErpCompany = () => {
  const { company } = useCompany();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(company, "No active company found");
  const erpCompanies = useSuspenseQuery(companiesQuery());

  // a bridge between ERP API and our API
  const activeErpCompany = erpCompanies.data.find(
    (c) =>
      c.country === company.country &&
      c.identificationNumber === company.number,
  );

  return activeErpCompany;
};

const useRequiredErpCompany = () => {
  const { company } = useCompany();
  // eslint-disable-next-line lingui/no-unlocalized-strings
  invariant(company, "No active company found");
  const erpCompanies = useSuspenseQuery(companiesQuery());

  // a bridge between ERP API and our API
  const activeErpCompany = erpCompanies.data.find(
    (c) =>
      c.country === company.country &&
      c.identificationNumber === company.number,
  );
  invariant(
    activeErpCompany,
    // eslint-disable-next-line lingui/no-unlocalized-strings
    `No matching ERP company found for company ${company.name}.\n
Country code: ${company.country}, company number: ${company.number}.
Available companies: ${erpCompanies.data.map((c) => `${c.country}: ${c.identificationNumber}`).join("\r\n")}`,
  );

  return activeErpCompany;
};

export { useErpCompany, useRequiredErpCompany };
